import { useEffect, useMemo, useState } from "react";
import PageLayout from "../components/PageLayout";
import { apiUrl } from "../services/api";

type PilotState = {
  telemetry?: Record<string, unknown>;
  now?: Record<string, unknown>;
  state?: Record<string, unknown>;
  metrics?: Record<string, unknown>;
  [key: string]: unknown;
};

type PilotStateSnapshot = {
  pilot_uid: string;
  pilot_name?: string | null;
  server_name?: string | null;
  trigger_event_type: string;
  trigger_event_at?: string | null;
  snapshot_at: string;
  unlocked_ids?: string[];
  state: PilotState;
};

type PilotHistoryPoint = {
  snapshotAt: string;
  values: Record<string, number>;
};

type WeaponTrack = {
  weaponKey?: string;
  weaponClass?: string;
  weaponName?: string | null;
  weaponDisplayName?: string | null;
  weaponObjectId?: number | string | null;
  targetObjectId?: number | string | null;
  inFlight?: boolean;
  status?: string | null;
  speedKts?: number | null;
  speedMach?: number | null;
  distanceNm?: number | null;
  ageSeconds?: number | null;
  hitAtMs?: number | null;
  lastSampleAtMs?: number | null;
};

const TOKEN_STORAGE_KEY = "checkride.admin.jwt";
const RETURN_HASH_KEY = "checkride.admin.return_hash";
const HISTORY_LIMIT = 120;
const ALL_SERVERS = "__all__";
const GRAPH_COLORS = [
  "#d4b140",
  "#64748b",
  "#22c55e",
  "#b52929",
  "#475569",
  "#166534",
  "#94a3b8",
  "#334155",
];

type FlatEntry = {
  key: string;
  value: unknown;
};

function flattenObject(input: unknown, prefix = ""): FlatEntry[] {
  if (!input || typeof input !== "object") {
    return prefix ? [{ key: prefix, value: input }] : [];
  }

  if (Array.isArray(input)) {
    return [{ key: prefix, value: JSON.stringify(input) }];
  }

  const entries: FlatEntry[] = [];
  const record = input as Record<string, unknown>;

  Object.keys(record)
    .sort()
    .forEach((field) => {
      const value = record[field];
      const nextKey = prefix ? `${prefix}.${field}` : field;

      if (value && typeof value === "object" && !Array.isArray(value)) {
        entries.push(...flattenObject(value, nextKey));
        return;
      }

      entries.push({ key: nextKey, value: value ?? null });
    });

  return entries;
}

function formatFieldValue(value: unknown): string {
  if (value == null) return "—";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "number") return Number.isFinite(value) ? `${value}` : "—";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return "—";
  }
}

function displayFieldKey(key: string): string {
  return key.replace(/^(telemetry|now|state|metrics|gauges)\./, "");
}

function isGraphableNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function formatNumber(value: unknown, digits = 1): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  return value.toFixed(digits);
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const normalized = token.trim().startsWith("Bearer ") ? token.trim().slice(7) : token.trim();
  const parts = normalized.split(".");
  if (parts.length < 2) return null;

  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    const json = atob(padded);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function isTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  const exp = payload?.exp;
  if (typeof exp !== "number") return false;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return exp <= nowSeconds;
}

function redirectToAdminSignIn() {
  const currentHash = window.location.hash || "#/admin/pilot_state";
  sessionStorage.setItem(RETURN_HASH_KEY, currentHash);
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  if (currentHash !== "#/admin/sign_in") {
    window.location.hash = "#/admin/sign_in";
  }
}

function cableUrlWithToken(token: string): string {
  const url = apiUrl("/cable");
  const protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.protocol = protocol;
  url.searchParams.set("token", token);
  return url.toString();
}

const AdminPilotStatePage = () => {
  const [status, setStatus] = useState<string>("connecting");
  const [error, setError] = useState<string | null>(null);
  const [rowsByPilot, setRowsByPilot] = useState<Record<string, PilotStateSnapshot>>({});
  const [historyByPilot, setHistoryByPilot] = useState<Record<string, PilotHistoryPoint[]>>({});
  const [selectedPilotUid, setSelectedPilotUid] = useState<string | null>(null);
  const [selectedServer, setSelectedServer] = useState<string>(ALL_SERVERS);
  const [graphKeys, setGraphKeys] = useState<string[]>([]);

  const token = useMemo(() => localStorage.getItem(TOKEN_STORAGE_KEY), []);

  useEffect(() => {
    if (!token) {
      redirectToAdminSignIn();
      return;
    }

    if (isTokenExpired(token)) {
      redirectToAdminSignIn();
      return;
    }

    let socket: WebSocket | null = null;
    let reconnectTimer: number | null = null;
    let closedByUser = false;

    const identifier = JSON.stringify({ channel: "PilotStateChannel" });

    const connect = () => {
      setStatus("connecting");
      socket = new WebSocket(cableUrlWithToken(token));

      socket.onopen = () => {
        setStatus("connected");
        setError(null);
        socket?.send(JSON.stringify({ command: "subscribe", identifier }));
      };

      socket.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (!payload?.message) return;

          const message = payload.message as PilotStateSnapshot;
          if (!message?.pilot_uid) return;

          const flatTelemetry = flattenObject(message.state?.telemetry || message.state?.now || {}, "telemetry");
          const flatState = flattenObject(message.state?.state || message.state?.metrics || {}, "state");
          const values = [...flatTelemetry, ...flatState].reduce<Record<string, number>>((accumulator, entry) => {
            if (isGraphableNumber(entry.value)) {
              accumulator[entry.key] = entry.value;
            }
            return accumulator;
          }, {});

          const historyPoint: PilotHistoryPoint = {
            snapshotAt: message.snapshot_at,
            values,
          };

          setRowsByPilot((prev) => ({
            ...prev,
            [message.pilot_uid]: message,
          }));

          setHistoryByPilot((prev) => {
            const existing = prev[message.pilot_uid] || [];
            const next = [
              ...existing,
              historyPoint,
            ].slice(-HISTORY_LIMIT);

            return {
              ...prev,
              [message.pilot_uid]: next,
            };
          });

          setSelectedPilotUid((prev) => prev || message.pilot_uid);
        } catch {
          // noop: ignore malformed websocket frames
        }
      };

      socket.onerror = () => {
        setStatus("error");
        setError("Pilot-state stream error.");
      };

      socket.onclose = (event) => {
        if (!closedByUser && (event.code === 1008 || event.code === 1006 || event.code === 4001)) {
          redirectToAdminSignIn();
          return;
        }

        if (closedByUser) return;
        setStatus("reconnecting");
        reconnectTimer = window.setTimeout(connect, 1500);
      };
    };

    connect();

    return () => {
      closedByUser = true;
      if (reconnectTimer !== null) {
        window.clearTimeout(reconnectTimer);
      }
      socket?.close();
    };
  }, [token]);

  const rows = useMemo(
    () =>
      Object.values(rowsByPilot).sort((a, b) =>
        new Date(b.snapshot_at).getTime() - new Date(a.snapshot_at).getTime()
      ),
    [rowsByPilot]
  );

  const servers = useMemo(() => {
    const unique = new Set(
      rows
        .map((row) => row.server_name)
        .filter((name): name is string => Boolean(name && name.trim().length > 0))
    );
    return [ALL_SERVERS, ...Array.from(unique).sort((a, b) => a.localeCompare(b))];
  }, [rows]);

  const visibleRows = useMemo(() => {
    if (selectedServer === ALL_SERVERS) return rows;
    return rows.filter((row) => row.server_name === selectedServer);
  }, [rows, selectedServer]);

  const selected = useMemo(() => {
    if (selectedPilotUid) {
      const match = visibleRows.find((row) => row.pilot_uid === selectedPilotUid);
      if (match) return match;
    }
    return visibleRows[0];
  }, [visibleRows, selectedPilotUid]);

  useEffect(() => {
    if (!selected) return;
    if (selectedPilotUid !== selected.pilot_uid) {
      setSelectedPilotUid(selected.pilot_uid);
    }
  }, [selected, selectedPilotUid]);

  useEffect(() => {
    if (selectedServer !== ALL_SERVERS && !servers.includes(selectedServer)) {
      setSelectedServer(ALL_SERVERS);
    }
  }, [selectedServer, servers]);

  const selectedHistory = selected ? historyByPilot[selected.pilot_uid] || [] : [];
  const selectedStateTelemetry = selected?.state.telemetry || selected?.state.now || {};
  const selectedStateData = selected?.state.state || selected?.state.metrics || {};
  const selectedStateGauges = selected?.state.gauges || {};
  const selectedStateDataPanel = useMemo(() => {
    if (!selectedStateData || typeof selectedStateData !== "object") return {};
    const { weapons, missiles, ...rest } = selectedStateData as Record<string, unknown>;
    return rest;
  }, [selectedStateData]);

  const weaponTracks = useMemo<WeaponTrack[]>(() => {
    if (!selectedStateData || typeof selectedStateData !== "object") return [];
    const raw = (selectedStateData as Record<string, unknown>).weapons;
    if (!Array.isArray(raw)) return [];

    return raw
      .filter((item): item is WeaponTrack => Boolean(item && typeof item === "object"))
      .sort((left, right) => {
        const leftActive = left.inFlight === true ? 1 : 0;
        const rightActive = right.inFlight === true ? 1 : 0;
        if (leftActive !== rightActive) return rightActive - leftActive;

        const leftUpdated = typeof left.lastSampleAtMs === "number" ? left.lastSampleAtMs : 0;
        const rightUpdated = typeof right.lastSampleAtMs === "number" ? right.lastSampleAtMs : 0;
        return rightUpdated - leftUpdated;
      });
  }, [selectedStateData]);

  const telemetryEntries = useMemo(() => flattenObject(selectedStateTelemetry, "telemetry"), [selectedStateTelemetry]);
  const stateEntries = useMemo(() => flattenObject(selectedStateDataPanel, "state"), [selectedStateDataPanel]);
  const gaugeEntries = useMemo(() => flattenObject(selectedStateGauges, "gauges"), [selectedStateGauges]);

  const graphableEntries = useMemo(() => {
    const all = [...telemetryEntries, ...stateEntries, ...gaugeEntries].filter((entry) => isGraphableNumber(entry.value));
    return all.sort((a, b) => a.key.localeCompare(b.key));
  }, [telemetryEntries, stateEntries, gaugeEntries]);

  useEffect(() => {
    setGraphKeys((prev) => prev.filter((key) => graphableEntries.some((entry) => entry.key === key)));
  }, [graphableEntries]);

  const seriesByKey = useMemo(() => {
    const next: Record<string, number[]> = {};
    graphKeys.forEach((key) => {
      next[key] = selectedHistory.map((point) => point.values[key]).filter((value): value is number => typeof value === "number");
    });
    return next;
  }, [graphKeys, selectedHistory]);

  const graphRange = useMemo(() => {
    const allValues = Object.values(seriesByKey).flat();
    if (allValues.length === 0) return null;
    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    return { min, max: max === min ? max + 1 : max };
  }, [seriesByKey]);

  const graphPaths = useMemo(() => {
    if (!graphRange || selectedHistory.length < 2 || graphKeys.length === 0) return [];

    const width = 520;
    const height = 120;
    const range = graphRange.max - graphRange.min || 1;

    return graphKeys.map((key, index) => {
      const points = selectedHistory.map((point) => point.values[key]);
      const path = points
        .map((value, pointIndex) => {
          if (!isGraphableNumber(value)) return null;
          const x = (pointIndex / Math.max(points.length - 1, 1)) * width;
          const y = height - ((value - graphRange.min) / range) * height;
          return `${pointIndex === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`;
        })
        .filter((segment): segment is string => Boolean(segment))
        .join(" ");

      return {
        key,
        color: GRAPH_COLORS[index % GRAPH_COLORS.length],
        path,
      };
      })
      .filter((item) => item.path.length > 0);
  }, [graphKeys, graphRange, selectedHistory]);

  const toggleGraphKey = (key: string) => {
    setGraphKeys((prev) => (prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]));
  };

  return (
    <PageLayout route="admin">
      <section className="panel panel-tight">
        <h2>Live Pilot State</h2>
        <p>Current in-memory pilot state snapshots from active clients.</p>
        <p className="status">Status: {status}</p>
        {error && <p className="alert">{error}</p>}

        <div className="admin-pilot-state-layout">
          <aside className="admin-pilot-state-sidebar">
            <label className="admin-pilot-state-server-filter">
              <span>Server</span>
              <select value={selectedServer} onChange={(event) => setSelectedServer(event.target.value)}>
                {servers.map((serverName) => (
                  <option key={serverName} value={serverName}>
                    {serverName === ALL_SERVERS ? "All" : serverName}
                  </option>
                ))}
              </select>
            </label>

            <h3>Pilots</h3>
            <ul className="admin-pilot-state-list">
              {visibleRows.length === 0 ? (
                <li className="admin-pilot-state-list-item">No pilot state updates yet.</li>
              ) : (
                visibleRows.map((row) => (
                  <li key={row.pilot_uid}>
                    <button
                      type="button"
                      className={`admin-pilot-state-pilot-btn ${selected?.pilot_uid === row.pilot_uid ? "is-active" : ""}`}
                      onClick={() => setSelectedPilotUid(row.pilot_uid)}
                    >
                      <span>{row.pilot_name || row.pilot_uid}</span>
                      <span>{new Date(row.snapshot_at).toLocaleTimeString()}</span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          </aside>

          <div className="admin-pilot-state-main">
            {selected ? (
              <>
                <div className="admin-pilot-state-head">
                  <h3>{selected.pilot_name || selected.pilot_uid}</h3>
                  <p>
                    Server: <strong>{selected.server_name || "unknown"}</strong> ·{" "}
                    Last event: <strong>{selected.trigger_event_type}</strong> at {new Date(selected.snapshot_at).toLocaleTimeString()}
                  </p>
                </div>

                <div className="admin-pilot-state-data-grid">
                  <section className="admin-pilot-state-data-panel">
                    <h4>Telemetry</h4>
                    <ul className="admin-pilot-state-data-list">
                      {telemetryEntries.length === 0 ? (
                        <li className="admin-pilot-state-data-row">
                          <span>—</span>
                          <span>no fields</span>
                        </li>
                      ) : (
                        telemetryEntries.map((entry) => (
                          <li key={entry.key} className="admin-pilot-state-data-row">
                            <span>{displayFieldKey(entry.key)}</span>
                            <span>{formatFieldValue(entry.value)}</span>
                          </li>
                        ))
                      )}
                    </ul>
                  </section>

                  <section className="admin-pilot-state-data-panel">
                    <h4>State</h4>
                    <ul className="admin-pilot-state-data-list">
                      {stateEntries.length === 0 ? (
                        <li className="admin-pilot-state-data-row">
                          <span>—</span>
                          <span>no fields</span>
                        </li>
                      ) : (
                        stateEntries.map((entry) => (
                          <li key={entry.key} className="admin-pilot-state-data-row">
                            <span>{displayFieldKey(entry.key)}</span>
                            <span>{formatFieldValue(entry.value)}</span>
                          </li>
                        ))
                      )}
                    </ul>
                  </section>

                  <section className="admin-pilot-state-data-panel">
                    <h4>Gauges</h4>
                    <ul className="admin-pilot-state-data-list">
                      {gaugeEntries.length === 0 ? (
                        <li className="admin-pilot-state-data-row">
                          <span>—</span>
                          <span>no fields</span>
                        </li>
                      ) : (
                        gaugeEntries.map((entry) => (
                          <li key={entry.key} className="admin-pilot-state-data-row">
                            <span>{displayFieldKey(entry.key)}</span>
                            <span>{formatFieldValue(entry.value)}</span>
                          </li>
                        ))
                      )}
                    </ul>
                  </section>
                </div>

                <section className="admin-pilot-state-weapons-panel">
                  <h4>Weapons</h4>
                  {weaponTracks.length === 0 ? (
                    <p className="status">No active or recent weapon tracks.</p>
                  ) : (
                    <div className="admin-pilot-state-weapons-table-wrap">
                      <table className="admin-pilot-state-weapons-table">
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>Category</th>
                            <th>ID</th>
                            <th>Initiator</th>
                            <th>Status</th>
                            <th>Speed (kts)</th>
                            <th>Mach</th>
                            <th>Distance (nm)</th>
                            <th>Target ID</th>
                            <th>Age (s)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {weaponTracks.map((weapon, index) => (
                            <tr key={weapon.weaponKey || `${weapon.weaponObjectId || "weapon"}-${index}`}>
                              <td>{weapon.weaponDisplayName || weapon.weaponName || weapon.weaponKey || "unknown"}</td>
                              <td>{weapon.weaponClass || "unknown"}</td>
                              <td>{weapon.weaponObjectId ?? weapon.weaponKey ?? "—"}</td>
                              <td>{selected.pilot_name || selected.pilot_uid}</td>
                              <td>{weapon.status || (weapon.inFlight ? "in_flight" : "complete")}</td>
                              <td>{formatNumber(weapon.speedKts, 1)}</td>
                              <td>{formatNumber(weapon.speedMach, 2)}</td>
                              <td>{formatNumber(weapon.distanceNm, 2)}</td>
                              <td>{weapon.targetObjectId ?? "—"}</td>
                              <td>{formatNumber(weapon.ageSeconds, 1)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>

                <div className="admin-pilot-state-graph-controls">
                  <h4>Graph</h4>
                  <ul className="admin-pilot-state-graph-field-list">
                    {graphableEntries.length === 0 ? (
                      <li className="admin-pilot-state-graph-field-item">No numeric fields available.</li>
                    ) : (
                      graphableEntries.map((entry) => {
                        const checked = graphKeys.includes(entry.key);
                        const color = checked
                          ? GRAPH_COLORS[graphKeys.findIndex((key) => key === entry.key) % GRAPH_COLORS.length]
                          : "transparent";

                        return (
                          <li key={entry.key} className="admin-pilot-state-graph-field-item">
                            <label>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleGraphKey(entry.key)}
                              />
                              <span className="admin-pilot-state-graph-color" style={{ backgroundColor: color }} />
                              <span>{displayFieldKey(entry.key)}</span>
                            </label>
                          </li>
                        );
                      })
                    )}
                  </ul>
                </div>

                {graphKeys.length > 0 && (
                  <div className="admin-pilot-state-graph-wrap">
                    {graphPaths.length > 0 ? (
                      <>
                        <svg viewBox="0 0 520 120" className="admin-pilot-state-graph" role="img" aria-label="Pilot state trend graph">
                          {graphPaths.map((series) => (
                            <path key={series.key} d={series.path} fill="none" stroke={series.color} strokeWidth="2" />
                          ))}
                        </svg>
                        <ul className="admin-pilot-state-graph-legend">
                          {graphPaths.map((series) => (
                            <li key={series.key}>
                              <span className="admin-pilot-state-graph-color" style={{ backgroundColor: series.color }} />
                              <span>{displayFieldKey(series.key)}</span>
                              <strong>{formatFieldValue(selectedHistory[selectedHistory.length - 1]?.values[series.key])}</strong>
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <p className="status">Need at least 2 samples to render graph.</p>
                    )}
                  </div>
                )}
              </>
            ) : (
              <p className="status">Waiting for pilot state snapshots…</p>
            )}
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default AdminPilotStatePage;
